// ============================================================================
// TOAST.JS - Notificación toast individual (tarjeta neutra + icono con color)
// ----------------------------------------------------------------------------
// Estilo limpio: tarjeta blanca, el color va SOLO en el badge del icono
// (verde éxito, rojo error, ámbar aviso, lavanda de marca para info). Título en
// negrita (= `message`) + descripción gris opcional (`options.description`).
// Mantiene: auto-dismiss (gestionado en ToastContext), botón "Deshacer", cerrar,
// y la variante `confirm` con Cancelar/Aceptar.
// ============================================================================

import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, X, Ban } from 'lucide-react';
import '../styles/toast.css';

// Badge del icono según el tipo. success/error/info/confirm = círculo de color con
// glifo blanco; warning = triángulo ámbar (icónico), como en las referencias.
function ToastIcon({ type }) {
  if (type === 'warning') {
    return <AlertTriangle className="toast-ico-tri" size={22} strokeWidth={2} aria-hidden="true" />;
  }
  let glyph;
  if (type === 'success') glyph = <Check size={13} strokeWidth={3.5} />;
  else if (type === 'error') glyph = <X size={13} strokeWidth={3.5} />;
  else if (type === 'confirm') glyph = <span className="toast-badge__txt">?</span>;
  else glyph = <span className="toast-badge__txt">i</span>; // info
  return (
    <span className={`toast-badge toast-badge--${type}`} aria-hidden="true">
      {glyph}
    </span>
  );
}

function Toast({ toast, onRemove, className = '' }) {
  // La entrada (slide) la hace el keyframe del .toast-wrapper; aquí solo el
  // fundido de salida.
  const [isRemoving, setIsRemoving] = useState(false);
  // Confirm: el foco va a «Cancelar» al aparecer (opción segura), así con
  // teclado o lector de pantalla no hay que recorrer la página para responder.
  const cancelRef = useRef(null);
  const isConfirm = toast.type === 'confirm';
  useEffect(() => {
    if (isConfirm) cancelRef.current?.focus({ preventScroll: true });
  }, [isConfirm]);

  const handleClose = () => {
    if (!toast.dismissible && toast.type !== 'confirm') return;
    setIsRemoving(true);
    setTimeout(() => onRemove(toast.id), 260); // = animación de salida
  };

  const handleConfirm = () => { if (toast.onConfirm) toast.onConfirm(); };
  const handleCancel = () => { if (toast.onCancel) toast.onCancel(); };

  // Título = `message` (retrocompat); descripción opcional.
  const title = toast.title || toast.message;
  const description = toast.description;

  const stateCls = isRemoving ? 'toast-removing' : '';

  // --- Toast de confirmación (reemplaza window.confirm) ---
  if (toast.type === 'confirm') {
    return (
      <div
        className={`toast toast-confirm ${stateCls} ${className}`}
        role="alertdialog"
        aria-label={typeof title === 'string' ? title : 'Confirmación'}
      >
        <div className="toast-content">
          <ToastIcon type="confirm" />
          <div className="toast-body">
            <div className="toast-title">{title}</div>
            {description && <div className="toast-desc">{description}</div>}
          </div>
        </div>
        <div className="toast-confirm-actions">
          <button ref={cancelRef} className="toast-confirm-btn toast-confirm-cancel" onClick={handleCancel}>
            <Ban size={15} />
            Cancelar
          </button>
          <button className="toast-confirm-btn toast-confirm-accept" onClick={handleConfirm}>
            <Check size={15} />
            Aceptar
          </button>
        </div>
      </div>
    );
  }

  // --- Toast normal (success, error, warning, info) ---
  return (
    <div className={`toast toast-${toast.type} ${stateCls} ${className}`}>
      <div className="toast-content">
        <ToastIcon type={toast.type} />

        <div className="toast-body">
          <div className="toast-title">{title}</div>
          {description && <div className="toast-desc">{description}</div>}
        </div>

        {toast.action && (
          <button
            className="toast-action"
            onClick={() => {
              if (typeof toast.action.onClick === 'function') toast.action.onClick();
              handleClose();
            }}
          >
            {toast.action.label || 'Deshacer'}
          </button>
        )}

        {toast.dismissible && (
          <button className="toast-close" onClick={handleClose} aria-label="Cerrar notificación">
            <X size={17} />
          </button>
        )}
      </div>
    </div>
  );
}

export default Toast;
